import React, { memo, useEffect, useRef, useState } from 'react';
import * as echarts from 'echarts';
import ReactECharts from 'echarts-for-react';

import * as _ from 'lodash';
import { mapIndexList } from 'utils/config';
import { log } from 'utils';

const appUrl = `${process.env.REACT_APP_ALGO_API_URL}`;

function usePrevious(value) {
  const ref = useRef();

  useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes

  return ref.current;
}

function ChartOverview(props) {
  const abortController = new AbortController();

  const DEFAULT_OPTION = {
    grid: { top: 8, right: 28, bottom: 20, left: 10 },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: [],
      // splitNumber: 5,
      // minInterval: 3600 * 1000,
      splitLine: {
        show: true,
        lineStyle: {
          type: 'dashed',
          color: '#4E4D62',
          dashOffset: 10,
          opacity: 0.3,
        },
      },
      axisTick: {
        alignWithLabel: true,
        length: 3,
      },
      axisLabel: {
        color: '#83817C',
        fontSize: 8,
        formatter: function (value, index) {
          const arr = value.split(':');
          return arr[0] + ':' + arr[1];
        },
        // width: 100,
        // overflow: 'truncate',
        interval: 60,
        borderColor: '#ECECEC',
        showMinLabel: true,
        showMaxLabel: true,
      },
    },
    yAxis: {
      type: 'value',
      show: true,
      position: 'right',
      alignTicks: true,
      splitNumber: 4,
      //   axisLine: {
      //     show: true,
      //     lineStyle: {
      //         type: 'dashed',
      //         color: '#4E4D62'
      //     }
      //   },
      splitLine: {
        // show: false,
        lineStyle: {
          type: 'dashed',
          color: '#4E4D62',
          dashOffset: 10,
          opacity: 0.3,
        },
      },
      axisLabel: {
        color: '#83817C',
        fontSize: 8,
        showMinLabel: false,
        showMaxLabel: false,
      },
    },
    series: [
      {
        type: 'line',
        smooth: true,
        symbol: 'none',
        sampling: 'lttb',
        itemStyle: {
          color: '#03CAA6',
        },
        lineStyle: {
          width: 1,
        },
        markLine: {
          symbol: ['none', 'none'],
          label: { show: false },
          lineStyle: {
            type: 'dashed',
            color: '#BEB8B8',
            dashOffset: 10,
            opacity: 0.5,
          },
          data: [{ yAxis: 1 }],
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            {
              offset: 0,
              color: '#20CA78',
            },
            {
              offset: 1,
              color: 'rgba(61, 176, 85, 0)',
            },
          ]),
        },
        data: [],
      },
    ],
    tooltip: false,
  };

  const [option, setOption] = useState(DEFAULT_OPTION);

  const { idata, symActive, algoIChart } = props;

  const preSymActive = usePrevious(symActive);
  const preIdata = usePrevious(idata);
  const preAlgoIChart = usePrevious(algoIChart);

  useEffect(() => {
    if (idata && !_.isEqual(idata, preIdata)) {
      const newOption = _.cloneDeep(option);
      // check đã được khởi tạo dữ liệu hay chưa
      if (!newOption.xAxis?.data) return;

      // push series
      const data0 = newOption.series[0].data;
      data0.push(idata.cIndex);

      // push time
      newOption.xAxis.data.push(idata.time);

      const _min = _.min(data0);
      const _max = _.max(data0);

      newOption.yAxis.min = _min;
      newOption.yAxis.max = _max;
      newOption.series[0].data = data0;

      if (idata?.cl === 'i') {
        // up
        newOption.series[0].itemStyle.color = '#03CAA6';
        newOption.series[0].areaStyle.color.colorStops[0].color = '#03CAA6';
        newOption.series[0].areaStyle.color.colorStops[1].color =
          'rgba(61, 176, 85, 0)';
      } else if (idata?.cl === 'd') {
        // down
        newOption.series[0].itemStyle.color = '#fd1515';
        newOption.series[0].areaStyle.color.colorStops[0].color = '#fd1515';
        newOption.series[0].areaStyle.color.colorStops[1].color =
          'rgba(253, 21, 21, 0)';
      } else {
        newOption.series[0].itemStyle.color = '#F0B90B';
        newOption.series[0].areaStyle.color.colorStops[0].color = '#F0B90B';
        newOption.series[0].areaStyle.color.colorStops[1].color =
          'rgba(240, 185, 11, 0)';
      }
      setOption(newOption);
    }
  }, [idata]);

  useEffect(() => {
    return () => {
      abortController.abort();
    };
  }, []);
  

  useEffect(() => {
    if(symActive && !_.isEqual(symActive, preSymActive)){
      _handleLoadDataStock();
    }
  }, [symActive]);
  
  useEffect(() => {
    if(algoIChart && !_.isEqual(algoIChart, preAlgoIChart))
      processRealtime(algoIChart)
  }, [algoIChart]);

  function _handleLoadDataStock() {
    // log(symActive, appUrl);
    if (!symActive) return;

    fetch(
      appUrl + 'pbapi/api/stockBoard/chart/index/' + mapIndexList[symActive],
      {
        signal: abortController.signal,
      }
    )
      .then((response) => response.json())
      .then((res) => {
        // // log(res);
        _mapDataToState(res.data);
      })
      .catch((error) => {
        log(error);
      });
  }

  function _mapDataToState(dt) {
    if (!dt.length) return;

    const newOption = _.cloneDeep(option);

    const yAxis = _.map(dt, 'IndexValue');
    const xAxis = _.map(dt, 'Time');

    const _min = _.min(yAxis);
    const _max = _.max(yAxis);

    // // log(newOption);

    newOption.xAxis.data = xAxis;
    newOption.yAxis.min = _min;
    newOption.yAxis.max = _max;
    newOption.series[0].data = yAxis;

    const _lastChange =
      dt[dt.length - 1].IndexValue - dt[dt.length - 1].IndexRefPrice;
    if (_lastChange > 0) {
      // up
      newOption.series[0].itemStyle.color = '#03CAA6';
      newOption.series[0].areaStyle.color.colorStops[0].color = '#03CAA6';
      newOption.series[0].areaStyle.color.colorStops[1].color =
        'rgba(61, 176, 85, 0)';
    } else if (_lastChange < 0) {
      // down
      newOption.series[0].itemStyle.color = '#fd1515';
      newOption.series[0].areaStyle.color.colorStops[0].color = '#fd1515';
      newOption.series[0].areaStyle.color.colorStops[1].color =
        'rgba(253, 21, 21, 0)';
    } else {
      newOption.series[0].itemStyle.color = '#F0B90B';
      newOption.series[0].areaStyle.color.colorStops[0].color = '#F0B90B';
      newOption.series[0].areaStyle.color.colorStops[1].color =
        'rgba(240, 185, 11, 0)';
    }

    // markLine
    newOption.series[0].markLine.data[0].yAxis = !!dt.length
      ? dt[0].IndexRefPrice
      : 1;

    console.log(newOption);

    setOption(newOption);
  }

  
  function processRealtime(record) {
    if(_.isArray(record) && record.length > 0 && record[0] === mapIndexList[symActive]){
      const newOption = _.cloneDeep(option);
      console.log(record)
      console.log(newOption)
      let xAxis = newOption.xAxis.data;
      let yAxis = newOption.series[0].data;

      // time
      xAxis.push(record[1])
      // data
      yAxis.push(record[2])
      const _min = _.min(yAxis);
      const _max = _.max(yAxis);
      newOption.yAxis.min = _min;
      newOption.yAxis.max = _max;
      newOption.series[0].data = yAxis;

      const _lastChange = record[2] - record[3];
      if (_lastChange > 0) {
        // up
        newOption.series[0].itemStyle.color = '#03CAA6';
        newOption.series[0].areaStyle.color.colorStops[0].color = '#03CAA6';
        newOption.series[0].areaStyle.color.colorStops[1].color =
          'rgba(61, 176, 85, 0)';
      } else if (_lastChange < 0) {
        // down
        newOption.series[0].itemStyle.color = '#fd1515';
        newOption.series[0].areaStyle.color.colorStops[0].color = '#fd1515';
        newOption.series[0].areaStyle.color.colorStops[1].color =
          'rgba(253, 21, 21, 0)';
      } else {
        newOption.series[0].itemStyle.color = '#F0B90B';
        newOption.series[0].areaStyle.color.colorStops[0].color = '#F0B90B';
        newOption.series[0].areaStyle.color.colorStops[1].color =
          'rgba(240, 185, 11, 0)';
      }
      setOption(newOption);
    }
  }


  return (
    <ReactECharts
      option={option}
      style={{
        height: '100%',
        width: `100%`,
      }}
    />
  );
}

export default memo(ChartOverview);
